import { NavLink, Navigate, Outlet, useLocation } from 'react-router-dom';
import { BarChart3, Bell, Clock, FlaskConical, LogOut, Moon, Radar, Route, Settings2, ShieldCheck, Waypoints, Sun, Truck, type LucideIcon } from 'lucide-react';
import { api } from '../api';
import { DatosProvider, useDatos } from '../state/Datos';
import { useSesion } from '../state/Sesion';
import { useTema } from '../state/Tema';
import { puedeVerReglas } from '../util/roles';
import { fmtHoraSeg, iniciales } from '../util/format';
import { useAhora } from '../util/hooks';
import { EstadoEnVivo } from './EstadoEnVivo';

const NAV: { a: string; etiqueta: string; icono: LucideIcon; fin?: boolean; soloAdmin?: boolean }[] = [
  { a: '/', etiqueta: 'Torre de control', icono: Radar, fin: true },
  { a: '/viajes', etiqueta: 'Viajes', icono: Route },
  { a: '/troncales', etiqueta: 'Troncales', icono: Waypoints },
  { a: '/alertas', etiqueta: 'Alertas', icono: Bell },
  { a: '/jornadas', etiqueta: 'Jornadas', icono: Clock },
  { a: '/flota', etiqueta: 'Flota', icono: Truck },
  { a: '/reportes', etiqueta: 'Reportes', icono: BarChart3 },
  { a: '/configuracion/alertas', etiqueta: 'Reglas', icono: Settings2, soloAdmin: true },
];

function Reloj() {
  const ahora = useAhora(1000);
  return (
    <span className="time" aria-label="Hora de Argentina">
      {fmtHoraSeg(ahora)}
    </span>
  );
}

function Topbar() {
  const { tema, alternar } = useTema();
  const { usuario, logout } = useSesion();
  const { indicadores } = useDatos();
  const claro = tema === 'light';
  const etiquetaTema = `Cambiar a tema ${claro ? 'oscuro' : 'claro'}`;
  return (
    <header className="topbar">
      <NavLink className="brand" to="/">
        <span className="brand-mark">A</span>AUSTRA<span className="brand-sub">TRANSPORT MANAGEMENT</span>
      </NavLink>
      <nav aria-label="Secciones">
        {NAV.filter((n) => !n.soloAdmin || puedeVerReglas(usuario?.rol)).map(({ a, etiqueta, icono: Icono, fin }) => (
          <NavLink key={a} to={a} end={fin} className={({ isActive }) => (isActive ? 'nav-active' : '')}>
            <Icono aria-hidden />
            {etiqueta}
            {a === '/alertas' && indicadores && indicadores.alertasAbiertas > 0 && <span className="chip red" style={{ padding: '0 6px' }}>{indicadores.alertasAbiertas}</span>}
          </NavLink>
        ))}
      </nav>
      <div className="top-end">
        {api.esMock && (
          <span className="demo" title="Los datos que ves son inventados y se generan en tu navegador.">
            <FlaskConical aria-hidden />
            Modo demostración · datos ficticios
          </span>
        )}
        <Reloj />
        <button type="button" className="icon" onClick={alternar} title={etiquetaTema} aria-label={etiquetaTema}>
          {claro ? <Moon aria-hidden /> : <Sun aria-hidden />}
        </button>
        <div className="user">
          <span className="avatar" aria-hidden>
            {iniciales(usuario?.nombre ?? '?')}
          </span>
          <span className="who">
            {usuario?.nombre}
            <small>{usuario?.rol}</small>
          </span>
          <button type="button" className="icon" onClick={logout} title="Cerrar sesión" aria-label="Cerrar sesión">
            <LogOut aria-hidden />
          </button>
        </div>
      </div>
    </header>
  );
}

function BarraEstado() {
  return (
    <footer className="statusbar">
      <span>
        <ShieldCheck aria-hidden />
        Centro de operaciones Trelew
      </span>
      <span>
        {api.esMock ? 'Telemetría simulada' : 'Telemetría Microtrack'} <span className="divider">|</span> Horarios de Argentina (UTC−3)
        <span className="divider">|</span>
        <EstadoEnVivo mostrarActualizacion={false} />
      </span>
    </footer>
  );
}

/** Estructura común de las pantallas autenticadas. Sin sesión redirige al login. */
export function Layout() {
  const { usuario } = useSesion();
  const ubicacion = useLocation();
  if (!usuario) return <Navigate to="/login" replace state={{ desde: ubicacion.pathname + ubicacion.search }} />;
  return (
    <DatosProvider>
      <Topbar />
      <Outlet />
      <BarraEstado />
    </DatosProvider>
  );
}
